import React from 'react';
import { Panel, Label } from '@uhg-harmony/uitk-react';
import { Arrow } from 'builder/scenes/SiteBuilder/SettingsPanel/SettingsPanel';
import {
  SizeLocationData,
  WidgetSizeLocation,
} from '../../../builder/components/WidgetSizeLocation';
import { Config } from './types';
import {
  Background,
  BackgroundData,
} from 'builder/components/WidgetConfigurations/Background/Background';
import {
  Padding,
  PaddingData,
} from 'builder/components/WidgetConfigurations/Padding';
import { Border } from 'builder/components/WidgetConfigurations/Border';
import { Slideshow } from 'builder/components/WidgetConfigurations/Slideshow/Slideshow';
import {
  DragDropBeautifulDnD,
  DragDropItemData,
} from 'builder/components/WidgetConfigurations/DragDropBeautifulDnD';
import { StyledPanel, StyledPanelTitle } from '../Text/TextWidgetControl';
import { SizeLocationPanel } from '../Link/LinkWidgetControl';
import { toast } from 'react-toastify';

const SlideshowWidgetControl: React.FC<WidgetControlProps<Config>> = (
  props: WidgetControlProps<Config>
) => {
  const {
    value,
    onChange,
    widgetState,
    addChild,
    currentPage,
    updateCurrentPage,
    removeWidget,
    isEditingTemplate,
    currentPage: { layoutLocked },
  } = props;

  const handleLineColor = data => {
    onChange({
      ...value,
      lineColor: data,
    });
  };

  const updateWidgetSizeLocation = (sizeLoc: SizeLocationData) => {
    onChange({
      ...value,
      width: sizeLoc.width,
      height: sizeLoc.height,
      xLocation: sizeLoc.xLocation,
      yLocation: sizeLoc.yLocation,
      uniqueId: sizeLoc.uniqueId,
    });
  };

  const updateBackgroundData = (data: BackgroundData) => {
    onChange({
      ...value,
      backgroundData: data,
    });
  };

  const updateBackgroundImage = (data: Image) => {
    onChange({
      ...value,
      backgroundImage: data,
    });
  };

  const updatePaddingData = (data: PaddingData) => {
    onChange({
      ...value,
      paddingData: data,
    });
  };

  const updateBorderData = (data: BorderData) => {
    onChange({
      ...value,
      borderData: data,
    });
  };

  const updateSlideshowData = (
    data: Array<DragDropItemData>,
    currentSlideIndex?: number
  ) => {
    const currentSlideIndexAfterDelete = currentSlideIndex
      ? currentSlideIndex
      : currentSlideIndex === 0
      ? 0
      : value.currentSlideIndex;
    onChange({
      ...value,
      slides: data,
      currentSlideIndex: currentSlideIndexAfterDelete,
    });
  };

  const handleArrowColor = (data: string) => {
    if (data) {
      if (data.startsWith('linear') || data.startsWith('radial')) {
        toast('Gradient cannot be used for arrow color', {
          type: 'warning',
          theme: 'colored',
        });
      } else {
        onChange({
          ...value,
          slideArrowColor: data,
        });
      }
    }
  };

  const handleSelectedDotsColor = (data: string) => {
    if (data) {
      if (data.startsWith('linear') || data.startsWith('radial')) {
        toast('Gradient cannot be used for positional dot color', {
          type: 'warning',
          theme: 'colored',
        });
      } else {
        onChange({
          ...value,
          slideSelectedDotsColor: data,
        });
      }
    }
  };

  const handleUnSelectedDotsColor = (data: string) => {
    if (data) {
      if (data.startsWith('linear') || data.startsWith('radial')) {
        toast('Gradient cannot be used for positional dot color', {
          type: 'warning',
          theme: 'colored',
        });
      } else {
        onChange({
          ...value,
          slideUnselectedDotsColor: data,
        });
      }
    }
  };

  const handleArrowsChecked = (data: Boolean) => {
    onChange({
      ...value,
      arrowsCheck: data,
    });
  };

  const handlePositionalDotsChecked = (data: Boolean) => {
    onChange({
      ...value,
      positionalDotsCheck: data,
    });
  };

  const handleAutoplayChecked = (data: Boolean) => {
    onChange({
      ...value,
      autoPlayChecked: data,
    });
  };

  const handleAutoplaySpeed = (data: string) => {
    onChange({
      ...value,
      autoPlayInterval: data,
    });
  };

  const handleSlideClick = (panelId: number) => {
    onChange(
      {
        ...value,
        currentSlideIndex: panelId,
      },
      widgetState.id
    );
  };

  const handleBackgroundColor = (data: string) => {
    onChange({
      ...value,
      backgroundData: { ...value.backgroundData, color: data },
    });
  };

  return (
    <>
      <Panel.Group>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>General</Label>}
            icon={Arrow}
            data-test-id="General-panel"
          >
            <Slideshow
              data-test-id="slideshow-component"
              disabled={layoutLocked}
              widgetState={widgetState}
              handleArrowColor={handleArrowColor}
              handleSelectedDotsColor={handleSelectedDotsColor}
              handleUnSelectedDotsColor={handleUnSelectedDotsColor}
              handleArrowsChecked={handleArrowsChecked}
              handleAutoplayChecked={handleAutoplayChecked}
              handleAutoplaySpeed={handleAutoplaySpeed}
              handlePositionalDotsChecked={handlePositionalDotsChecked}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
            />
            <DragDropBeautifulDnD
              dragDropItemsData={updateSlideshowData}
              itemsState={widgetState.config.slides}
              itemName={'Slide'}
              heading={'Slides'}
              addChild={addChild}
              widgetState={widgetState}
              currentPage={currentPage}
              updateCurrentPage={updateCurrentPage}
              updateCurrentTab={handleSlideClick}
              removeWidget={removeWidget}
              isEditingTemplate={isEditingTemplate}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <SizeLocationPanel
            title={<Label style={StyledPanelTitle}>Size and Location</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="size-location-panel"
          >
            <WidgetSizeLocation
              data-test-id="widget-size-location"
              disabled={layoutLocked}
              sizeLocation={{
                xLocation: value.xLocation,
                yLocation: value.yLocation,
                width: value.width,
                height: value.height,
                uniqueId: value.uniqueId,
              }}
              updateWidgetSizeLocation={updateWidgetSizeLocation}
              activeWidgetId={widgetState.id}
            />
          </SizeLocationPanel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>Background</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="background-panel"
          >
            <Background
              data-test-id="background-component"
              disabled={layoutLocked}
              backgroundData={updateBackgroundData}
              backgroundImage={updateBackgroundImage}
              handleBackgroundColor={handleBackgroundColor}
              widgetState={widgetState}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>Border</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="border-panel"
          >
            <Border
              data-test-id="border-data"
              updateBorderData={updateBorderData}
              disabled={layoutLocked}
              handleLineColor={handleLineColor}
              widgetState={widgetState}
              pages={props.pages}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>Padding</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="padding-panel"
          >
            <Padding
              data-test-id="padding-data"
              disabled={layoutLocked}
              paddingData={updatePaddingData}
              widgetState={widgetState}
            />
          </Panel>
        </StyledPanel>
      </Panel.Group>
    </>
  );
};

export default SlideshowWidgetControl;
